/**
 * 수발주자 위치 게이트 ($j.F.Mobile.LocationGate)
 *
 * 세션당 1회, 수발주자에게 두 단계를 순서대로 통과시킨다.
 *   1) 위치정보 활용 동의 — LOCATION_SERVICE · LOCATION_PRIVACY_POLICY 현행버전 미동의면 BS 로 받는다.
 *                          법적 동의라 앱·PC 어디로 들어왔든 받는다.
 *                          서버가 photo=1 을 주면(소유자 · 사진 활용 미응답) 같은 시트 아래에
 *                          「사진 활용 동의」(선택, MARKETING) 구역을 얹어 한 번에 받는다 — 바로 켜고 끄고, › 로 전문.
 *   2) Android GPS 권한   — 동의를 마쳤으면 권한 보유를 확인하고, 없으면 안내 토스트를 띄운다.
 *                          앱 전용 단계다. 앱 밖(PC 브라우저)에서는 getLocationPermission() 이 null
 *                          (=알 수 없음)이라 저절로 건너뛴다 — 개발환경 PC 도 마찬가지로 안 띄운다.
 *
 * 호출: 모바일 사이트 부트스트랩(Sites/Mobile/Bootstrap)에서 매 페이지 start().
 *   로그인 후 어느 화면으로 진입하든(딥링크 포함) 걸리게 하고, 반복 노출은 세션 플래그로 막는다.
 *
 * 대리로그인 차단은 서버(LocationConsentService::isProxySession)가 한다.
 *
 * 동의 확인만 세션 1회고(서버 조회), 권한 확인은 진입할 때마다 한다 — start() 주석 참고.
 */
import Util from '../../Common/Util';
const SESSION_KEY = 'locGate';    // sessionStorage — `{loginKey}:{ok|no}` 동의 확인 결과 (로그인마다 무효화)
const POP_KEY     = 'locGatePop'; // sessionStorage — 권한 안내 토스트를 띄운 loginKey
const POP_DURATION = 5000;       // 하단 안내 토스트 자동 닫힘(ms) — 진행바 길이와 동일
const POP_ANIM     = 340;        // 슬라이드 아웃 시간(ms) — scss transition 과 동기

export default class LocationGate {
    constructor() {
        this.items = [];      // [{tag, title}] — 동의 항목
        this.agreed = {};     // {tag: true} — 체크 상태
        this.photo = { ask: false, on: false };   // 사진 활용(선택) — 서버가 물으라 했는가 · 켜졌는가
        this.$pop = null;     // 하단 안내 토스트 (열려 있을 때만)
        this._popTimer = null;
    }

    /* ───────────────── 진입 ───────────────── */

    /**
     * 게이트 시작. 대상이 아니면 조용히 빠진다.
     *
     * 두 단계의 주기가 다르다 —
     *   · 동의 확인 : 서버 조회라 세션 1회 (결과를 세션에 남겨 재조회를 막는다)
     *   · 권한 확인 : 앱에 동기로 묻는 호출이라 비용이 없다. 진입할 때마다 한다.
     *                 그래야 "동의 후 나중에 권한을 끈" 경우도 잡힌다.
     */
    start = () => {
        if (!this._isMember()) return;

        const state = this._sessionState();
        if (state === 'ok')    { this.checkPermission(); return; } // 동의 확인 끝 — 권한만 본다
        if (state === 'shown') return;                            // 이번 세션 이미 안내함 (BS 또는 토스트)

        $.ajax({ url: '/m/ajax/location-consent', method: 'GET', dataType: 'json' })
            .done((res) => {
                if (!res || !res.result) return;
                if (res.need != 1) { this._setSessionState('ok'); this.checkPermission(); return; }

                // 미동의 — 이번 세션 1회 안내. (동의 시 _submit 이 'ok' 로 덮는다)
                this._sheetData = res;             // 토스트 CTA 에서 BS 재오픈용
                this._setSessionState('shown');
                if (res.prompted == 1) this._showConsentPop();   // 이후: 토스트 권유(비블로킹)
                else this._openSheet(res);                       // 첫 회: 블로킹 BS
            });
    };

    /**
     * 게이트를 적용할 회원인가.
     *
     * 앱 여부는 보지 않는다 — 동의는 법적 동의라 PC 로 들어온 본인에게도 받아야 하고,
     * 앱 전용인 GPS 권한 단계는 getLocationPermission() 이 null(=알 수 없음)을 돌려주므로
     * 웹에서는 저절로 건너뛴다. 대리로그인 차단은 서버(LocationConsentService::isProxySession)가 한다.
     */
    _isMember = () => {
        if (typeof MEMBER === 'undefined' || !MEMBER || !MEMBER.num) return false;
        return MEMBER.mem_driver_type === '수발주자';   // 위치 수집 대상은 수발주자뿐
    };

    /**
     * 이번 로그인의 식별자. sessionStorage 는 탭 단위라 로그아웃→재로그인(같은 탭)에도 살아남는다.
     * 회원번호로 키를 잡으면 같은 회원이 재로그인해도 옛 플래그가 남아 영영 안 돈다 → 로그인마다 바뀌는
     * loginKey(서버 발급)로 잡아야 재로그인 시 자동 무효화된다. loginKey 없으면(구앱 등) 회원번호로 폴백.
     */
    _lk = () => String((typeof MEMBER !== 'undefined' && MEMBER && (MEMBER.loginKey || MEMBER.num)) || '');

    /**
     * 이 로그인에서 동의 확인이 끝났는지. '' | 'ok'(동의됨) | 'no'(거부함)
     * 값에 loginKey 를 함께 담아 재로그인 시 자동 무효화한다.
     */
    _sessionState = () => {
        try {
            const [lk, state] = String(sessionStorage.getItem(SESSION_KEY) || '').split(':');
            return (lk === this._lk()) ? (state || '') : '';
        } catch (e) { return ''; }      // 접근 불가 → 매번 조회
    };

    _setSessionState = (state) => {
        try { sessionStorage.setItem(SESSION_KEY, `${this._lk()}:${state}`); } catch (e) { /* 용량초과 무시 */ }
    };

    /* ───────────────── 1. 위치정보 활용 동의 ───────────────── */

    _openSheet = (res) => {
        const self = this;
        this.items  = res.items || [];
        this.agreed = {};
        this.photo  = { ask: res.photo == 1, on: false };

        $j.Ui.BS.open({
            target: {
                init() {
                    const $cont = $('#' + this.id);
                    // 제목은 시트 본문 첫 줄에 직접 그린다(좌측 정렬) → BS 기본 헤더는 숨긴다.
                    // 필수 동의라 X 로도, 드래그로도 닫히면 안 되는데 헤더가 곧 드래그 닫기 손잡이라 헤더째 걷는다.
                    // 거부는 '동의안함' 버튼으로만 (이력이 남는다).
                    $('.bottomsheet-popup').addClass('lc-bs');
                    $cont.html(self._sheetHtml(res.mode || ''));
                    self._bind($cont);
                },
                destroy() {
                    $('.bottomsheet-popup').removeClass('lc-bs');   // BS 는 단일 인스턴스라 다른 시트에 남지 않게
                    $('#' + this.id).off('.lc');
                },
            },
        });
    };

    _sheetHtml = (mode) => {
        // 각 약관은 행(또는 ›)을 눌러 전문을 끝까지 읽어야 동의(체크)된다 → 직접 체크·전체동의 없음.
        // 두 구역 모드는 구역 제목에 「필수」 태그가 있어 행의 [필수] 접두어를 빼 중복을 피한다 (같은 정보는 한 번)
        const req = this.photo.ask ? '' : '<b class="lc-req">[필수]</b> ';
        const rows = this.items.map((it) => `
            <div class="lc-row js-row" data-tag="${esc(it.tag)}" role="button" tabindex="0">
                <span class="lc-cb" aria-hidden="true"></span>
                <span class="lc-label">${req}${esc(it.title)}</span>
                <button type="button" class="lc-view js-view" data-tag="${esc(it.tag)}" aria-label="${esc(it.title)} 전문 보기">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
                </button>
            </div>`).join('');

        const actions = `
            <div class="lc-actions">
                <button type="button" class="lc-decline js-decline">동의안함</button>
                <button type="button" class="lc-cta js-agree" disabled>동의</button>
            </div>`;
        // 제목은 왜 뜬 시트인지(장비보유회원 전환/등록) 한 줄로 — 무엇에 동의하는지는 아래 항목이 말한다
        const heading = `<div class="lc-head"><h3 class="lc-heading">${esc(mode || '위치정보 활용 동의')}</h3></div>`;

        // 사진 활용(선택)을 같이 묻지 않으면 — 위치 동의만 (소속자 · 이미 답한 소유자)
        if (!this.photo.ask) {
            return `
            <div class="lc-sheet">
                ${heading}
                <p class="lc-desc">근처 현장의 오더를 받는 데 필요해요.</p>
                <div class="lc-rows">${rows}</div>
                ${actions}
            </div>`;
        }

        // 두 구역 — 위치정보 활용(필수·읽어야 체크) + 사진 활용(선택·바로 토글). 라벨 「사진 활용 동의」= MARKETING
        return `
        <div class="lc-sheet">
            ${heading}

            <section class="lc-sec lc-sec--first">
                <h4 class="lc-sec__t">위치정보 활용 <span class="lc-tag is-must">필수</span></h4>
                <p class="lc-desc">근처 현장의 오더를 받는 데 필요해요.</p>
                <div class="lc-rows">${rows}</div>
            </section>
            <section class="lc-sec">
                <h4 class="lc-sec__t">작업사진 활용 동의 <span class="lc-tag">선택</span></h4>
                <p class="lc-desc">동의하면 <b>수주포인트</b> 적립</p>
                <div class="lc-rows">
                    <div class="lc-row js-photo" role="checkbox" aria-checked="false" tabindex="0">
                        <span class="lc-cb" aria-hidden="true"></span>
                        <span class="lc-label">작업사진 활용 동의</span>
                        <button type="button" class="lc-view js-photo-view" aria-label="작업사진 활용 동의 전문 보기">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
                        </button>
                    </div>
                </div>
            </section>
            ${actions}
        </div>`;
    };

    _bind = ($cont) => {
        const self = this;
        this._$cont = $cont;   // _openTerm 이 동의 후 _sync 하려고 시트 컨테이너 보관

        // 행/› 클릭 → 전문 뷰어 열기 (직접 체크 불가 — 읽어야 동의된다).
        // '›' 버튼 클릭도 .js-row 로 버블돼 이 핸들 하나가 처리한다.
        $cont.on('click.lc', '.js-row', function () {
            self._openTerm($(this).data('tag'));
        });

        // 사진 활용(선택) — 행은 바로 켜고 끈다. › 는 전문만 연다(거기서 [동의합니다]를 눌러도 켜진다)
        $cont.on('click.lc', '.js-photo-view', function (e) {
            e.stopPropagation();
            self._openTerm('mkterm');
        });
        $cont.on('click.lc', '.js-photo', function () {
            self.photo.on = !self.photo.on;
            self._sync($cont);
        });
        $cont.on('keydown.lc', '.js-photo', function (e) {
            if (e.target !== this) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); $(this).trigger('click'); }
        });

        $cont.on('click.lc', '.js-agree', function () {
            if ($(this).prop('disabled')) return;
            self._submit($(this), 1);
        });

        // 동의안함 — 실수 방지 확인. '동의 안 함'을 골라야 실제 거부. 취소는 BS 유지.
        $cont.on('click.lc', '.js-decline', function () {
            const $btn = $(this);
            const ask = () => self._submit($btn, 0);
            // 메시지는 title 로 넘긴다 — 다이얼로그의 본문 타이포가 그 슬롯이다(pre-line·keep-all).
            // 배열/text 로 넘기면 한 단계 작은 desc 슬롯으로 들어가 글씨가 작아진다.
            Util.swalert(
                '동의하지 않으면 근처 오더를 매칭받을 수 없어요.\n지금은 동의 안 하시겠어요?',
                null,                    // 취소(기본) → 아무것도 안 함, BS 유지
                ['취소', '동의 안 함'],
                ask                      // '동의 안 함' → 실제 거부
            );
        });
    };

    _isAll = () => this.items.length > 0 && this.items.every((it) => !!this.agreed[it.tag]);

    /** 동의(=읽음) 상태를 시트에 반영. 모든 약관 동의 시 하단 '동의' 버튼 활성. */
    _sync = ($cont) => {
        $cont.find('.js-row').each((i, el) => {
            const $row = $(el);
            const on = !!this.agreed[$row.data('tag')];
            $row.find('.lc-cb').toggleClass('is-on', on);
            $row.toggleClass('is-agreed', on);
        });
        $cont.find('.js-photo .lc-cb').toggleClass('is-on', this.photo.on);
        $cont.find('.js-photo').attr('aria-checked', this.photo.on ? 'true' : 'false');
        $cont.find('.js-agree').prop('disabled', !this._isAll());
    };

    /**
     * 약관 전문 뷰어 — 끝까지 스크롤(다 읽음)해야 하단 '동의' 버튼이 활성.
     * 동의를 누르면 그 약관이 체크되고 닫힌다. BS 는 단일 인스턴스라 전체화면 모달로 띄운다.
     *
     * 하단 버튼은 다 읽기 전에도 **누를 수 있다** — 누르면 본문을 맨 아래로 부드럽게 내려준다
     * (은행앱 방식). 손가락으로 긴 약관을 끝까지 쓸어내리는 수고를 없앤다. 바닥에 닿으면
     * scroll 이벤트가 tryEnable 을 돌려 '동의합니다'로 바뀐다.
     * ⚠ disabled 속성을 쓰면 클릭 이벤트 자체가 안 오므로 잠금은 `.is-locked` 클래스로 한다.
     */
    _openTerm = (tag) => {
        const self = this;
        const isPhoto = tag === 'mkterm';   // 사진 활용(선택) 전문 — 동의 항목 목록(items) 밖
        const item = isPhoto ? { tag, title: '작업사진 활용 동의' } : this.items.find((it) => it.tag === tag);
        const already = isPhoto ? this.photo.on : !!this.agreed[tag];

        // panelClass — 본문(TermsContent)이 글자색을 페이지 테마 토큰으로 찍는다.
        // 모달 패널은 기본이 흰색 고정이라 다크에서 흰글씨/흰바탕이 된다 → 패널도 표면 토큰을 타게 한다.
        const modal = $j.Ui.Modal.open({
            mode: 'full',
            panelClass: 'lc-doc-modal',
            title: item ? item.title : '약관',
            html: '<div class="lc-doc">불러오는 중…</div>',
        });

        const $body = modal.el.find('.ui-modal__body');
        const $foot = $('<div class="lc-doc-foot">'
            + '<button type="button" class="lc-doc-agree is-locked">끝까지 읽어주세요 <span aria-hidden="true">↓</span></button>'
            + '</div>');
        $body.after($foot);                       // body(스크롤) 아래 고정 바
        const $agree = $foot.find('.lc-doc-agree');
        let ro = null;
        let loaded = false;   // 전문이 도착하기 전('불러오는 중…')은 짧아서 바닥이다 → 그때 활성되면 안 된다
        const enable = () => { $agree.removeClass('is-locked').text('동의합니다'); if (ro) { ro.disconnect(); ro = null; } };
        const atBottom = () => {
            const el = $body[0];
            return el.scrollHeight - el.scrollTop - el.clientHeight <= 8; // 바닥 근처(짧아서 스크롤 불필요 포함)
        };
        // 이미 동의했거나 바닥이면 활성. 활성됐으면 true.
        const tryEnable = () => { if (already || (loaded && atBottom())) { enable(); return true; } return false; };

        // 잠금 상태에서 누르면 본문을 맨 아래로. smooth 는 스크롤 이벤트가 계속 오므로 도착하면
        // tryEnable 이 저절로 돌지만, behavior 미지원 웹뷰·중간에 멈춘 경우를 대비해 지연 재판정도 건다.
        const scrollToBottom = () => {
            if (!loaded) return;              // 아직 불러오는 중 — 내릴 것도 없다
            const el = $body[0];
            const top = el.scrollHeight - el.clientHeight;
            try { el.scrollTo({ top, behavior: 'smooth' }); } catch (e) { el.scrollTop = top; }
            setTimeout(tryEnable, 400);
            setTimeout(tryEnable, 900);
        };

        $body.on('scroll.lc', tryEnable);   // 스크롤이 바닥에 닿으면(=다 읽음) 활성

        $agree.on('click', () => {
            if ($agree.hasClass('is-locked')) { scrollToBottom(); return; }   // 아직 다 안 읽음 — 내려준다
            if (isPhoto) self.photo.on = true; else self.agreed[tag] = true;
            self._sync(self._$cont);
            modal.close();
        });

        $.ajax({ url: '/m/ajax/modal/term', method: 'POST', data: { tag } })
            .done((html) => {
                modal.setBody(`<div class="lc-doc">${html || '내용이 없습니다.'}</div>`);
                loaded = true;
                if (tryEnable()) return;
                // 웹뷰는 모달 레이아웃(clientHeight)이 늦게 잡혀 즉시 판정이 이르다 —
                // body 크기가 확정되는 순간 재판정(ResizeObserver) + 지연 폴백.
                if (window.ResizeObserver) { ro = new ResizeObserver(() => tryEnable()); ro.observe($body[0]); }
                setTimeout(tryEnable, 200);
                setTimeout(tryEnable, 600);
            })
            .fail(() => modal.setBody('<div class="lc-doc">약관을 불러오지 못했습니다.</div>'));
    };

    /**
     * 동의(1)/거부(0) 저장 → 시트를 닫고, 동의한 경우에만 권한 단계로 넘어간다.
     * 사진 활용(선택)을 같이 물었으면 그 답(photo 0|1)도 보낸다 — 서버는 위치에 동의한 경우에만 기록한다.
     */
    _submit = ($btn, agree) => {
        $btn.addClass('is-loading').prop('disabled', true);

        const data = { agree };
        if (this.photo.ask) data.photo = this.photo.on ? 1 : 0;

        $.ajax({ url: '/m/ajax/location-consent', method: 'POST', dataType: 'json', data })
            .done((res) => {
                $j.Ui.BS.close();
                if (!res || !res.result) {
                    $j.Ui.Toast.show('저장하지 못했습니다. 잠시 후 다시 시도해주세요.', { type: 'error' });
                    return;
                }
                this._setSessionState(agree ? 'ok' : 'shown');
                if (agree) {
                    if (this.photo.ask && typeof MEMBER !== 'undefined' && MEMBER) MEMBER.is_ad = this.photo.on ? 2 : 1;   // 포인트 화면 토글이 읽는 캐시
                    this.checkPermission();
                    return;
                }
                // 거부 — 데드엔드 대신 권유 토스트로 전환(세션 내에서도 '동의하기'로 복귀 가능).
                this._showConsentPop();
            })
            .fail(() => {
                $btn.removeClass('is-loading').prop('disabled', false);
                $j.Ui.Toast.show('네트워크 오류로 저장하지 못했습니다.', { type: 'error' });
            });
    };

    /* ───────────────── 2. Android GPS 권한 ───────────────── */

    /**
     * GPS 권한 확인 → 없으면 안내 토스트.
     *
     * 권한창(requestLocationPermission)을 여기서 바로 띄우지 않는다. 맥락 없이 시스템 다이얼로그가
     * 튀어나오면 반사적으로 거부하기 쉬워서, 왜 필요한지 먼저 알리고 사용자가 '위치 켜기'를
     * 눌렀을 때 요청한다.
     *
     * getLocationPermission() 이 null 이면 "권한 없음"이 아니라 "알 수 없음"(구앱·웹 환경)이므로
     * 아무 안내도 하지 않는다 — 오안내가 된다. (F/AppBridge.js 계약 주석 참고)
     */
    /**
     * @param {boolean} [force] 세션 1회 제한을 무시하고 다시 띄운다 (개발중 확인용)
     *                          예: 콘솔에서 `$j.F.Mobile.LocationGate.checkPermission(true)`
     */
    checkPermission = (force = false) => {
        // 앱 밖(PC)에서는 null — 권한을 알 수 없으므로 아무 안내도 하지 않는다
        const granted = $j.F.App.getLocationPermission();
        if (granted === null || granted === 1) return;
        this._showPermissionPop(force);
    };

    /**
     * 하단 안내 토스트 — 스크림 없이 뜨고, 진행바가 다 줄면 자동으로 닫힌다.
     * 로그인당 1회만 띄운다(loginKey 기준). 부트스트랩에서 매 페이지 호출되므로 이 제한이 없으면
     * 화면을 넘길 때마다 다시 뜬다. 재로그인하면 loginKey 가 바뀌어 다시 뜬다(권한 여전히 없으면).
     */
    _showPermissionPop = (force = false) => {
        if (!force) {
            try {
                if (sessionStorage.getItem(POP_KEY) === this._lk()) return;
                sessionStorage.setItem(POP_KEY, this._lk());
            } catch (e) { /* 접근 불가 → 제한 없이 띄운다 */ }
        }
        this._openPop('위치정보가 꺼져 있어 당일 오더를 받을 수 없어요', '위치 켜기', () => this._onClickPopCta());
    };

    /**
     * 약관 권유 토스트 (이후 로그인·거부 후) — 비블로킹. '동의하기' 누르면 약관 BS 를 연다.
     * 첫 회 블로킹 BS 대신 쓰는 부드러운 재안내 (.ai/proxy-login.md 아님 — LocationGate 자체 흐름).
     */
    _showConsentPop = () => {
        this._openPop('위치정보 약관에 동의하면 근처 오더를 받을 수 있어요', '동의하기', () => {
            this._closePop();
            if (this._sheetData) this._openSheet(this._sheetData);
        });
    };

    /** 하단 토스트 공통 빌더 — 메시지·CTA·CTA핸들만 다르다. 진행바 자동 닫힘 공유. */
    _openPop = (msg, ctaText, onCta) => {
        this._closePop();   // 중복 방지

        const $pop = $(`
        <div class="lc-pop" role="alert" aria-live="assertive">
            <div class="lc-pop__line">
                <span class="lc-pop__msg">${esc(msg)}</span>
                <button type="button" class="lc-pop__cta js-pop-cta">${esc(ctaText)}</button>
            </div>
            <div class="lc-pop__prog"><i></i></div>
        </div>`).appendTo('body');

        this.$pop = $pop;
        // 슬라이드업과 자동 닫힘 타이머를 같은 프레임에서 시작한다.
        // rAF 는 화면이 보일 때 발화하므로, 앱이 백그라운드에서 웹뷰를 띄워둔 경우
        // 타이머만 먼저 흘러 "보이기도 전에 닫혀 있는" 상황을 막는다(setTimeout 은 백그라운드에서도 흐른다).
        requestAnimationFrame(() => {
            $pop.addClass('is-on');
            this._startPopTimer();
        });

        $pop.on('click', '.js-pop-cta', onCta);
    };

    /** 진행바를 8초에 걸쳐 0 으로 줄이고, 끝나면 닫는다 */
    _startPopTimer = () => {
        if (!this.$pop) return;
        const $bar = this.$pop.find('.lc-pop__prog i');
        $bar.css({ transition: 'none', width: '100%' });
        $bar[0].offsetWidth;                                    // reflow — 이게 없으면 애니메이션이 안 걸린다
        $bar.css({ transition: `width ${POP_DURATION}ms linear`, width: '0%' });

        clearTimeout(this._popTimer);
        this._popTimer = setTimeout(this._closePop, POP_DURATION);
    };

    /** 권한창을 띄우는 동안엔 자동 닫힘을 멈춘다 (진행바도 현재 폭에서 정지) */
    _stopPopTimer = () => {
        clearTimeout(this._popTimer);
        if (!this.$pop) return;
        const $bar = this.$pop.find('.lc-pop__prog i');
        if ($bar.length) $bar.css({ transition: 'none', width: $bar[0].getBoundingClientRect().width + 'px' });
    };

    _closePop = () => {
        clearTimeout(this._popTimer);
        const $pop = this.$pop;
        if (!$pop) return;
        this.$pop = null;
        $pop.removeClass('is-on');
        setTimeout(() => $pop.remove(), POP_ANIM);
    };

    /** '위치 켜기' — 여기서 비로소 시스템 권한창을 요청한다 */
    _onClickPopCta = () => {
        this._stopPopTimer();
        const $btn = this.$pop.find('.js-pop-cta').addClass('is-loading');

        // 사용자의 선택은 콜백으로 1회 회신된다 (callbackLocationPermission → EventBus)
        $j.Common.EventBus.once($j.getNS(this), 'app:location:permission', (e, data) => {
            $btn.removeClass('is-loading');
            if (data && data.granted == 1) { this._closePop(); return; }
            this._popToSettingsGuide();
        });

        // false = 앱이 권한창을 못 띄운다(구앱·웹). 콜백도 오지 않으므로 여기서 바로 안내로 전환.
        if (!$j.F.App.requestLocationPermission()) {
            $j.Common.EventBus.offEvent('app:location:permission', $j.getNS(this));
            $btn.removeClass('is-loading');
            this._popToSettingsGuide();
        }
    };

    /**
     * 거부됐을 때의 안내. 안드로이드는 '다시 묻지 않음' 이후로는 요청해도 시스템 창이 뜨지 않아
     * 버튼을 남겨두면 눌러도 아무 일이 없다 — 버튼을 걷고 수동 안내로 바꾼다.
     * (앱에 '설정 화면 열기' broker 액션이 생기면 그때 버튼을 되살릴 자리)
     */
    _popToSettingsGuide = () => {
        if (!this.$pop) return;
        // 메뉴 경로는 기종마다 다르다(안드로이드 '설정 > 앱 > 장비불러 > 권한', 아이폰 '설정 > 장비불러 > 위치') — 공통 문구로
        this.$pop.find('.lc-pop__msg').text('휴대폰 설정에서 장비불러 앱의 위치 권한을 허용해주세요');
        this.$pop.find('.js-pop-cta').remove();
        this._startPopTimer();
    };
}

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}
